import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type MacroEngineIndicator = "CPI" | "Fed Funds";

export type MacroEnginePoint = {
  date: string; // YYYY-MM-DD
  realValue: number | null;
  marketExpectation: number | null;
};

type AnyRecord = Record<string, unknown>;

function toNumberOrNull(v: unknown): number | null {
  if (v == null) return null;
  const n = typeof v === "string" ? Number(v) : (v as number);
  return Number.isFinite(n) ? n : null;
}

function toDateString(v: unknown): string | null {
  if (typeof v !== "string") return null;
  // Accept YYYY-MM-DD or ISO timestamp
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

function normalizePayload(payload: unknown): MacroEnginePoint[] {
  const arr = Array.isArray(payload)
    ? payload
    : (payload as AnyRecord | null)?.data && Array.isArray((payload as AnyRecord).data)
      ? ((payload as AnyRecord).data as unknown[])
      : (payload as AnyRecord | null)?.series && Array.isArray((payload as AnyRecord).series)
        ? ((payload as AnyRecord).series as unknown[])
        : [];

  const points: MacroEnginePoint[] = [];

  for (const item of arr) {
    if (!item || typeof item !== "object") continue;
    const r = item as AnyRecord;

    const date =
      toDateString(r.date) ??
      toDateString(r.timestamp) ??
      toDateString(r.ts) ??
      null;

    if (!date) continue;

    const realValue =
      toNumberOrNull(r.realValue) ??
      toNumberOrNull(r.real_value_fred) ??
      toNumberOrNull(r.value) ??
      null;

    const marketExpectation =
      toNumberOrNull(r.marketExpectation) ??
      toNumberOrNull(r.predicted_value_poly) ??
      toNumberOrNull(r.market_expectation) ??
      null;

    points.push({ date, realValue, marketExpectation });
  }

  points.sort((a, b) => a.date.localeCompare(b.date));
  return points;
}

function filterLastYears(points: MacroEnginePoint[], years: number): MacroEnginePoint[] {
  const cutoff = new Date();
  cutoff.setFullYear(cutoff.getFullYear() - years);
  const cutoffISO = cutoff.toISOString().slice(0, 10);
  return points.filter((p) => p.date >= cutoffISO);
}

export function useMacroEngine(indicator: MacroEngineIndicator, years = 5) {
  return useQuery({
    queryKey: ["macro-engine", indicator, years],
    staleTime: 1000 * 60 * 10,
    retry: 2,
    queryFn: async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      const url = new URL(
        `https://pafnmppyisnhfqfozjag.supabase.co/functions/v1/macro-engine`
      );
      url.searchParams.set("action", "view");
      url.searchParams.set("indicator", indicator);

      // Edge Functions typically accept apikey + Authorization.
      // If verify_jwt is enabled, we send the user token; otherwise anon still works.
      const token = session?.access_token;

      const res = await fetch(url.toString(), {
        method: "GET",
        headers: {
          apikey:
            "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBhZm5tcHB5aXNuaGZxZm96amFnIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTYxNTIwNzksImV4cCI6MjA3MTcyODA3OX0.Jmu-5CvQ8lxBEdJp0IEoP75t1JQenViMBHI8MqciTAE",
          Authorization: `Bearer ${
            token ??
            "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBhZm5tcHB5aXNuaGZxZm96amFnIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTYxNTIwNzksImV4cCI6MjA3MTcyODA3OX0.Jmu-5CvQ8lxBEdJp0IEoP75t1JQenViMBHI8MqciTAE"
          }`,
        },
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`macro-engine failed (${res.status}): ${text || res.statusText}`);
      }

      const json = (await res.json()) as unknown;
      const normalized = normalizePayload(json);
      return filterLastYears(normalized, years);
    },
  });
}
